/**
 * The view column headers of the visibility matrix: one focus toggle, one
 * `scope · behaviour` line and one Show / Hide bulk pair per view, plus the
 * frozen label header. Consumed by `VisibilityMatrix`, which owns selection, the
 * plan and every write. The scope line carries what the `always` and `Form` tags
 * used to say and more — `org · pdf · internal`, `form · —`, `other · locked` —
 * so a column's origin and behaviour read in one place. The focus `Tooltip`
 * mounts only when it has a title: an editable view without a `longDescription`
 * would otherwise pay for an empty one on every column.
 * The bulk plan is computed only for the open confirmation, so ~40 columns cost
 * nothing at rest. The confirmation states the truth — other views keep their
 * setting, `internal` is unaffected, rows already in that state are counted,
 * rows carrying the stored `hidden` sentinel are skipped and counted, and a
 * hide on a row shown everywhere writes the other views out explicitly. The
 * word "overwrite" is false here and must not appear.
 */
import { FC, useState } from 'react';
import { Button, Popconfirm, Tooltip, Typography, theme } from 'antd';
import type { VisibilityTypeId } from '#types';
import FlexBox from '../../atoms/FlexBox';
import FlexCol from '../../atoms/FlexCol';
import type { VisibilityMatrixBulkPlan, VisibilityMatrixHeaderProps } from './VisibilityMatrix.types';

const { Text } = Typography;

const rowsAre = (count: number) => (count === 1 ? '1 row is' : `${count} rows are`);

const bulkLines = (plan: VisibilityMatrixBulkPlan, viewLabel: string, selectedCount: number): string[] =>
    [
        `${plan.updates.length} of ${selectedCount} selected rows change.`,
        plan.alreadyInStateCount > 0
            ? `${rowsAre(plan.alreadyInStateCount)} already ${plan.shown ? 'shown in' : 'hidden from'} ${viewLabel}.`
            : '',
        plan.lastShownViewCount > 0
            ? `${rowsAre(plan.lastShownViewCount)} kept as they are, because ${viewLabel} is the only view left showing them.`
            : '',
        plan.hiddenColumnCount > 0
            ? `${rowsAre(plan.hiddenColumnCount)} hidden outright and never appear in any view; nothing here can change that.`
            : '',
        !plan.shown && plan.shownEverywhereCount > 0
            ? `${rowsAre(plan.shownEverywhereCount)} shown in every view today; hiding ${viewLabel} writes the other views out explicitly, so a view added later will not appear on them.`
            : '',
        'Every other view keeps its current setting.',
        'The internal view is unaffected.',
    ].filter((line) => line.length > 0);

export const VisibilityMatrixHeader: FC<VisibilityMatrixHeaderProps> = ({
    columns,
    gridTemplateColumns,
    labelColumnWidth,
    selectedCount,
    focusViewId,
    onFocusViewChange,
    planBulk,
    onApplyBulk,
    disabled,
}) => {
    const { token } = theme.useToken();
    const [pendingBulk, setPendingBulk] = useState<{ viewId: VisibilityTypeId; shown: boolean } | undefined>(
        undefined,
    );

    const plan = pendingBulk ? planBulk(pendingBulk.viewId, pendingBulk.shown) : undefined;

    const bulkButton = (viewId: VisibilityTypeId, viewLabel: string, shown: boolean, editable: boolean) => {
        const open = pendingBulk?.viewId === viewId && pendingBulk.shown === shown;
        const action = shown ? 'Show' : 'Hide';
        return (
            <Popconfirm
                open={open}
                title={shown ? `Show in ${viewLabel}` : `Hide from ${viewLabel}`}
                description={
                    open && plan ? (
                        <FlexCol gap={5} style={{ maxWidth: token.controlHeight * 10 }}>
                            {bulkLines(plan, viewLabel, selectedCount).map((line) => (
                                <Text key={line} type="secondary">
                                    {line}
                                </Text>
                            ))}
                        </FlexCol>
                    ) : null
                }
                okText={action}
                okButtonProps={{ disabled: open && plan ? plan.updates.length === 0 : false }}
                onConfirm={() => {
                    if (plan) onApplyBulk(plan);
                    setPendingBulk(undefined);
                }}
                onCancel={() => setPendingBulk(undefined)}
                onOpenChange={(next) => {
                    if (!next) setPendingBulk(undefined);
                }}
            >
                <Button
                    size="small"
                    disabled={disabled || !editable || selectedCount === 0}
                    onClick={() => setPendingBulk({ viewId, shown })}
                    aria-label={
                        shown
                            ? `Show selected rows in ${viewLabel}`
                            : `Hide selected rows from ${viewLabel}`
                    }
                >
                    {action}
                </Button>
            </Popconfirm>
        );
    };

    return (
        <div
            role="row"
            style={{
                display: 'grid',
                gridTemplateColumns,
                position: 'sticky',
                top: 0,
                zIndex: 2,
                backgroundColor: token.colorBgContainer,
                borderBottom: `1px solid ${token.colorBorder}`,
            }}
        >
            <div
                role="columnheader"
                style={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 3,
                    width: labelColumnWidth,
                    boxSizing: 'border-box',
                    padding: `${token.paddingXS}px ${token.paddingSM}px`,
                    backgroundColor: token.colorBgContainer,
                }}
            >
                <Text strong>Object</Text>
            </div>
            {columns.map(({ view, origin, editable }) => {
                const focused = focusViewId === view.id;
                const scope = origin === 'org' ? 'org' : origin === 'own' ? 'form' : 'other';
                const flags = [view.isPdfExport === true ? 'pdf' : '', view.internalVisibility === true ? 'internal' : '']
                    .filter((flag) => flag.length > 0)
                    .join(' · ');
                const behaviour = editable ? (flags.length > 0 ? flags : '—') : 'locked';
                const description = editable
                    ? (view.longDescription ?? '')
                    : `${view.shortLabel} always shows every object.`;
                const focusButton = (
                    <Button
                        size="small"
                        type={focused ? 'primary' : 'text'}
                        disabled={disabled || !editable}
                        aria-pressed={focused}
                        onClick={() => onFocusViewChange(focused ? undefined : view.id)}
                        aria-label={`Focus ${view.shortLabel} column`}
                    >
                        {view.shortLabel}
                    </Button>
                );
                return (
                    <div
                        key={view.id}
                        role="columnheader"
                        style={{
                            padding: `${token.paddingXXS}px ${token.paddingXS}px`,
                            minWidth: 0,
                            overflow: 'hidden',
                            boxSizing: 'border-box',
                            backgroundColor: focused ? token.controlItemBgActive : undefined,
                        }}
                    >
                        <FlexCol gap={5}>
                            <FlexBox gap={5} justifyStart noGrow>
                                {description.length > 0 ? (
                                    <Tooltip title={description}>{focusButton}</Tooltip>
                                ) : (
                                    focusButton
                                )}
                            </FlexBox>
                            <Text type="secondary">{`${scope} · ${behaviour}`}</Text>
                            <FlexBox gap={5} justifyStart noGrow>
                                {bulkButton(view.id, view.shortLabel, true, editable)}
                                {bulkButton(view.id, view.shortLabel, false, editable)}
                            </FlexBox>
                        </FlexCol>
                    </div>
                );
            })}
        </div>
    );
};
